/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-06
 */

// Add third-party dependencies.
import { useState, useCallback } from 'react';

// Add local dependencies.
import type { ValidationRules, ValidationErrors } from '../utils/validation';

/**
 * Configuration options for the form validation hook.
 */
interface UseFormValidationOptions {
  /** Whether to validate fields in real-time as user types */
  validateOnChange?: boolean;
  /** Whether to validate fields when they lose focus */
  validateOnBlur?: boolean;
  /** Whether to validate all fields on form submission */
  validateOnSubmit?: boolean;
}

/**
 * Return type for the useFormValidation hook.
 */
interface UseFormValidationReturn<T> {
  /** Current form errors */
  errors: ValidationErrors;
  /** Whether the form is currently valid (no errors) */
  isValid: boolean;
  /** Validate a specific field */
  validateField: (
    fieldName: keyof T,
    value: any,
    formData?: T
  ) => string | undefined;
  /** Validate all fields in the form */
  validateForm: (formData: T) => boolean;
  /** Clear errors for a specific field */
  clearFieldError: (fieldName: keyof T) => void;
  /** Clear all form errors */
  clearAllErrors: () => void;
  /** Set a custom error for a field */
  setFieldError: (fieldName: keyof T, error: string) => void;
  /** Handle input change with optional validation */
  handleInputChange: (fieldName: keyof T, value: any, formData?: T) => void;
  /** Handle input blur with optional validation */
  handleInputBlur: (fieldName: keyof T, value: any, formData?: T) => void;
}

/**
 * Custom hook for managing form validation state and logic.
 * Provides a consistent way to validate forms across the application.
 *
 * @param validationRules - Object defining validation rules for each field
 * @param options - Configuration options for validation behavior
 * @returns Object with validation state and helper functions
 *
 * @example
 * const validationRules = {
 *   email: [validateEmail],
 *   password: [validatePassword],
 *   confirmPassword: [(value, formData) => validatePasswordMatch(formData.password, value)]
 * };
 *
 * const { errors, isValid, validateForm, handleInputChange } = useFormValidation(
 *   validationRules,
 *   { validateOnChange: true }
 * );
 */
export const useFormValidation = <T extends Record<string, any>>(
  validationRules: ValidationRules,
  options: UseFormValidationOptions = {}
): UseFormValidationReturn<T> => {
  const {
    validateOnChange = false,
    validateOnBlur = true,
    validateOnSubmit = true,
  } = options;

  const [errors, setErrors] = useState<ValidationErrors>({});

  /**
   * Validates a specific field using its validation rules.
   */
  const validateField = useCallback(
    (fieldName: keyof T, value: any, formData?: T): string | undefined => {
      const fieldRules = validationRules[fieldName as string];
      if (!fieldRules || fieldRules.length === 0) {
        return undefined;
      }

      // Run all validation rules for this field
      const validationErrors = fieldRules
        .map(rule => rule(value, formData))
        .filter(error => error !== undefined);

      if (validationErrors.length > 0) {
        return validationErrors[0];
      }

      return undefined;
    },
    [validationRules]
  );

  /**
   * Validates all fields in the form.
   */
  const validateForm = useCallback(
    (formData: T): boolean => {
      if (!validateOnSubmit) {
        return true;
      }

      const newErrors: ValidationErrors = {};
      let hasErrors = false;

      // Validate each field that has validation rules
      Object.keys(validationRules).forEach(fieldName => {
        const value = formData[fieldName];
        const error = validateField(fieldName as keyof T, value, formData);

        if (error) {
          newErrors[fieldName] = error;
          hasErrors = true;
        }
      });

      setErrors(newErrors);
      return !hasErrors;
    },
    [validationRules, validateField, validateOnSubmit]
  );

  /**
   * Clears the error for a specific field.
   */
  const clearFieldError = useCallback((fieldName: keyof T) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName as string];
      return newErrors;
    });
  }, []);

  /**
   * Clears all form errors.
   */
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Sets a custom error for a specific field.
   */
  const setFieldError = useCallback((fieldName: keyof T, error: string) => {
    setErrors(prev => ({
      ...prev,
      [fieldName as string]: error,
    }));
  }, []);

  /**
   * Handles input change events with optional validation.
   */
  const handleInputChange = useCallback(
    (fieldName: keyof T, value: any, formData?: T) => {
      // Clear existing error for this field
      clearFieldError(fieldName);

      // Validate on change if enabled
      if (validateOnChange && formData) {
        const error = validateField(fieldName, value, formData);
        if (error) {
          setFieldError(fieldName, error);
        }
      }
    },
    [validateOnChange, validateField, clearFieldError, setFieldError]
  );

  /**
   * Handles input blur events with optional validation.
   */
  const handleInputBlur = useCallback(
    (fieldName: keyof T, value: any, formData?: T) => {
      // Validate on blur if enabled
      if (validateOnBlur && formData) {
        const error = validateField(fieldName, value, formData);
        if (error) {
          setFieldError(fieldName, error);
        } else {
          clearFieldError(fieldName);
        }
      }
    },
    [validateOnBlur, validateField, setFieldError, clearFieldError]
  );

  // Calculate if form is valid (no errors)
  const isValid = Object.keys(errors).length === 0;

  return {
    errors,
    isValid,
    validateField,
    validateForm,
    clearFieldError,
    clearAllErrors,
    setFieldError,
    handleInputChange,
    handleInputBlur,
  };
};

export default useFormValidation;
